// 登录页的 Cloudflare Turnstile 人机验证组件（决策 D173，docs/04 第 9 节）。脚本按需加载，只有启用了人机验证的登录页才请求 Cloudflare。
import { useEffect, useRef } from "react";
import { currentTheme } from "@/app/theme";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** 与后端核验的 action 一致，别的页面签发的令牌不能拿来登录。 */
const TURNSTILE_ACTION = "admin-login";

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let loading: Promise<TurnstileApi> | null = null;

/** 加载 Turnstile 脚本，整个页面只加载一次；失败后允许下次重试。 */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile 脚本没有就绪")));
    s.onerror = () => reject(new Error("Turnstile 脚本加载失败"));
    document.head.appendChild(s);
  }).catch((e: unknown) => {
    loading = null;
    throw e;
  });
  return loading;
}

export interface TurnstileProps {
  siteKey: string;
  /** 拿到令牌时给出令牌；令牌过期、出错或被重置时给 null。 */
  onToken: (token: string | null) => void;
  /** 组件加载失败或验证出错时的说明。 */
  onError: (message: string) => void;
  /** 变化时重置组件：令牌只能用一次，登录失败后要重新验证。 */
  resetKey: number;
}

/** 渲染 Turnstile 组件。宽度随登录面板（flexible），深浅取渲染时后台实际生效的主题（D200）；之后切换主题不重建组件，免得已拿到的令牌作废。 */
export function Turnstile({ siteKey, onToken, onError, resetKey }: TurnstileProps) {
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  // 回调放进 ref：组件只按站点密钥渲染一次，父组件每次重渲染换新函数不应重建组件
  const cb = useRef({ onToken, onError });
  cb.current = { onToken, onError };

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !box.current) return;
      widget.current = ts.render(box.current, {
        sitekey: siteKey,
        action: TURNSTILE_ACTION,
        size: "flexible",
        theme: currentTheme(),
        language: "zh-cn",
        callback: (token: string) => cb.current.onToken(token),
        "expired-callback": () => cb.current.onToken(null),
        "error-callback": () => {
          cb.current.onToken(null);
          cb.current.onError("人机验证出错，请刷新页面重试");
        },
      });
    }, () => {
      if (!cancelled) cb.current.onError("人机验证加载失败，请检查网络能否访问 challenges.cloudflare.com 后刷新页面");
    });
    return () => {
      cancelled = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey]);

  useEffect(() => {
    if (resetKey === 0 || !widget.current) return;
    cb.current.onToken(null);
    window.turnstile?.reset(widget.current);
  }, [resetKey]);

  return <div ref={box} />;
}
